import { getPermittedProviders } from 'src/store/actions/providerActions';
import { getPermittedPartners } from 'src/store/actions/partnerActions';
import { getPermittedDistributors } from 'src/store/actions/distributorActions';

// localStorage keys that the HTTP client turns into the X-PROVIDER-ID /
// X-PARTNER-ID / X-DISTRIBUTOR-ID context headers on every request.
const CONTEXT_STORAGE_KEYS = ['activeProviderId', 'activePartnerId', 'activeDistributorId'];

// Permitted scopes, in the priority order we resolve them. `isActive` reports
// whether this scope actually ended up with an active entity in the store.
const SCOPES = [
  {
    name: 'provider',
    action: getPermittedProviders,
    isActive: (state) => Boolean(state.provider.activeProvider)
  },
  {
    name: 'partner',
    action: getPermittedPartners,
    isActive: (state) => Boolean(state.partner.activePartner)
  },
  {
    name: 'distributor',
    action: getPermittedDistributors,
    isActive: (state) => Boolean(state.distributor.activeDistributor)
  }
];

/**
 * Resolves the logged-in user's active context after authentication by trying
 * each permitted scope (providers → partners → distributors) in order.
 *
 * A scope counts as resolved ONLY when it leaves an active entity in the store
 * (its *_SUCCESS reducer only selects one when the list is non-empty). So we
 * treat all of these as "not this scope, keep going":
 *   - an HTTP error (e.g. a 403 for a scope the user doesn't belong to)
 *   - a 200 with an empty array (a valid response, but no entity to act as)
 * The first scope that actually resolves wins and the rest are skipped.
 *
 * This replaces four hand-rolled, subtly different copies of this logic that
 * branched on hard-coded error message strings and therefore broke for external
 * users (most notably distributors) whenever an upstream scope errored or came
 * back empty instead of throwing the exact expected message.
 *
 * @returns {Function} thunk resolving to the active scope name, or null
 */
export default function resolveActiveContext() {
  return async (dispatch, getState) => {
    // Drop any stale context header from a previous session/role BEFORE we
    // resolve. The HTTP client attaches X-PROVIDER-ID / X-PARTNER-ID /
    // X-DISTRIBUTOR-ID from localStorage to every request — including the
    // permitted lookups below and the getUserById call FETCH_ROLES makes next.
    // A leftover provider header makes those 401 ("no permissions") for a
    // partner/distributor. Each scope's reducer re-sets its own key when it
    // resolves, so the winner ends up with exactly one correct header.
    CONTEXT_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

    // eslint-disable-next-line no-restricted-syntax
    for (const scope of SCOPES) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(scope.action());
      } catch (error) {
        // Empty list or not permitted — fall through to the next scope.
        console.error(`Could not resolve ${scope.name} context:`, error?.message);
      }
      if (scope.isActive(getState())) {
        return scope.name;
      }
    }
    return null;
  };
}
